/**
 * Auth · Welcome — LOTE M (2026-02-26).
 *
 * Web adaptation of the original Expo screen. Structural mapping:
 *   View                 → <div>
 *   Text                 → <span> / <h1>
 *   Pressable            → <button>
 *   ImageBackground      → outer <div> with backgroundImage cover
 *   LinearGradient       → inner <div> with CSS linear-gradient overlay
 *   SafeAreaView         → padded flex container (no notch on web)
 *   expo-router          → react-router-dom (useNavigate)
 *   useApp()             → inline lang state mirroring _layout.tsx
 *   LanguagePicker       → minimal LangPill (same UX as the marketing navbar)
 *   Stack.Screen title   → document.title update via useEffect
 *
 * All copy, colours, gradient stops, font sizes, paddings, radii, button
 * heights and behaviour are preserved verbatim from the Expo source.
 * Buttons keep their original testIDs as `data-testid`.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { wwwTheme, wwwRadius } from '@/src/website/theme';
import {
  WWW_T, WWW_LOCALES, WWW_LOCALE_LABEL, detectBrowserLang, type WwwLang,
} from '@/src/website/i18n';

const LS_KEY = 'mf_www_lang';

// Web replacement for `useApp().isDark`. The marketing site is dark-only
// (wwwTheme === palette.dark), so isDark is always true — matching the
// Expo theme that powered this screen when the user opened it from the
// dark-themed app shell.
const IS_DARK = true;

export default function Welcome() {
  const navigate = useNavigate();

  // Inline lang detection that mirrors WwwLayout's logic so the welcome
  // screen — which renders outside WwwLayout — keeps the same language
  // preference the visitor already chose on the marketing pages.
  const [lang, setLangState] = useState<WwwLang>('es');
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(LS_KEY) as WwwLang | null;
      if (stored && WWW_LOCALES.includes(stored)) { setLangState(stored); return; }
    } catch { /* ignore */ }
    setLangState(detectBrowserLang());
  }, []);

  const setLang = (l: WwwLang) => {
    setLangState(l);
    try { window.localStorage.setItem(LS_KEY, l); } catch { /* ignore */ }
  };

  const t = WWW_T[lang];

  // Web equivalent of <Stack.Screen options={{ title: ... }} />.
  useEffect(() => {
    document.title = 'STILGIA · ' + t.welcome;
  }, [t.welcome]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: wwwTheme.bg }}>
      {/* ImageBackground → background-image cover */}
      <div
        style={{
          minHeight: '100vh',
          backgroundImage:
            'url(https://images.unsplash.com/photo-1708397016786-8916880649b8?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* LinearGradient colors=['transparent', #09090BCC, #09090B] locations=[0,0.5,1] */}
        <div
          style={{
            minHeight: '100vh',
            background: `linear-gradient(180deg, transparent 0%, ${IS_DARK ? '#09090BCC' : '#0A0A0ACC'} 50%, ${IS_DARK ? '#09090B' : '#0A0A0A'} 100%)`,
            position: 'relative',
          }}
        >
          {/* SafeAreaView edges=['top','bottom'] with padding: 24, flex: 1, justifyContent: 'space-between' */}
          <div
            style={{
              minHeight: '100vh',
              padding: 24,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <span data-testid="brand-title" style={styles.brand}>STILGIA</span>
            </div>
            <div>
              <h1 data-testid="welcome-headline" style={styles.headline}>{t.welcome}</h1>
              <div style={{ height: 32 }} />
              <button
                data-testid="cta-signup"
                onClick={() => navigate('/(auth)/signup')}
                style={styles.primaryBtn}
              >
                <span style={styles.primaryBtnText}>{t.signUp}</span>
              </button>
              <button
                data-testid="cta-signin"
                onClick={() => navigate('/(auth)/login')}
                style={styles.outlineBtn}
              >
                <span style={styles.outlineBtnText}>{t.signIn}</span>
              </button>
            </div>
          </div>

          {/* LanguagePicker top={8} right={24} — minimal pill + dropdown, same UX as the marketing navbar */}
          <div style={{ position: 'absolute', top: 8, right: 24, zIndex: 10 }}>
            <button
              data-testid="lang-picker-toggle"
              onClick={() => setLangOpen(o => !o)}
              style={styles.langPill}
            >
              <span style={{ color: '#FAFAFA', fontSize: 12, fontWeight: 600, letterSpacing: '0.5px' }}>
                {lang.toUpperCase()}
              </span>
            </button>
            {langOpen && (
              <div style={styles.langMenu}>
                {WWW_LOCALES.map(l => (
                  <button
                    key={l}
                    data-testid={`lang-picker-option-${l}`}
                    onClick={() => { setLang(l); setLangOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      paddingTop: 10, paddingBottom: 10, paddingLeft: 14, paddingRight: 14,
                      background: 'transparent', border: 'none', cursor: 'pointer',
                    }}
                  >
                    <span style={{ color: l === lang ? '#FAFAFA' : '#A1A1AA', fontWeight: l === lang ? 600 : 400, fontSize: 13 }}>
                      {WWW_LOCALE_LABEL[l]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Verbatim translation of the original StyleSheet.create(...) block.
const styles: Record<string, React.CSSProperties> = {
  brand:           { color: '#FAFAFA', fontSize: 22, letterSpacing: '4px', fontWeight: 500 },
  headline:        { color: '#FAFAFA', fontSize: 44, lineHeight: '48px', fontWeight: 600, marginTop: 0, marginBottom: 8 },
  sub:             { color: '#D4D4D8', fontSize: 16, lineHeight: '24px' },
  primaryBtn:      { backgroundColor: '#FAFAFA', height: 54, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, width: '100%', border: 'none', cursor: 'pointer', padding: 0 },
  primaryBtnText:  { color: '#09090B', fontSize: 16, fontWeight: 600 },
  outlineBtn:      { borderColor: '#FAFAFA40', borderWidth: 1, borderStyle: 'solid', height: 54, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', background: 'transparent', cursor: 'pointer', padding: 0 },
  outlineBtnText:  { color: '#FAFAFA', fontSize: 16, fontWeight: 500 },
  langPill:        { borderWidth: 1, borderStyle: 'solid', borderColor: '#FAFAFA40', borderRadius: wwwRadius.pill, paddingLeft: 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6, background: 'transparent', cursor: 'pointer' },
  langMenu:        { position: 'absolute', top: 38, right: 0, borderWidth: 1, borderStyle: 'solid', borderColor: '#27272A', borderRadius: wwwRadius.md, minWidth: 140, boxShadow: '0 8px 24px rgba(0,0,0,0.32)', backgroundColor: '#121214' },
};
